import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';


const UserBehaviorAnalysis = ({ events }) => {
  const [userAnalysis, setUserAnalysis] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    analyzeUserBehavior();
  }, [events]);

  const analyzeUserBehavior = () => {
    // Group events by user
    const userEvents = {};
    
    events?.forEach(event => {
      const userId = event?.user_id;
      if (!userId) return;
      
      if (!userEvents?.[userId]) {
        userEvents[userId] = {
          user: event?.user,
          events: [],
          riskScore: 0,
          suspiciousActivities: 0
        };
      }
      
      userEvents?.[userId]?.events?.push(event);
      if (event?.severity === 'critical' || event?.severity === 'error') {
        userEvents[userId].riskScore += 20;
      }
      if (event?.requires_immediate_action) {
        userEvents[userId].suspiciousActivities++;
      }
    });

    // Convert to array and sort by risk score
    const analysis = Object.values(userEvents)?.map(userData => ({
        ...userData,
        riskScore: Math.min(100, userData?.riskScore),
        eventCount: userData?.events?.length
      }))?.sort((a, b) => b?.riskScore - a?.riskScore);

    setUserAnalysis(analysis);
  };

  const getRiskColor = (score) => {
    if (score >= 75) return 'var(--color-error)';
    if (score >= 50) return 'var(--color-warning)';
    if (score >= 25) return 'var(--color-info)';
    return 'var(--color-success)';
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* User List */}
      <div className="lg:col-span-2 card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-heading font-semibold text-foreground">
            User Behavior Analysis
          </h3>
          <span className="text-sm text-muted-foreground">
            {userAnalysis?.length || 0} users analyzed
          </span>
        </div>

        <div className="space-y-3">
          {userAnalysis?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No user activity data available</p>
            </div>
          ) : (
            userAnalysis?.map((userData, index) => (
              <div
                key={index}
                onClick={() => setSelectedUser(userData)}
                className={`p-4 rounded-lg border cursor-pointer transition-all duration-250 ${
                  selectedUser === userData
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary">
                        {userData?.user?.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground">
                        {userData?.user?.full_name || 'Unknown User'}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {userData?.user?.email || 'No email'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Risk Score</p>
                      <p
                        className="text-lg font-semibold"
                        style={{ color: getRiskColor(userData?.riskScore) }}
                      >
                        {userData?.riskScore}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Events</p>
                      <p className="text-lg font-semibold text-foreground">{userData?.eventCount}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* User Details */}
      <div className="card">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          User Activity Details
        </h3>

        {selectedUser ? (
          <div className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-lg font-medium text-primary">
                    {selectedUser?.user?.full_name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground">
                    {selectedUser?.user?.full_name || 'Unknown User'}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {selectedUser?.user?.email || 'No email'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Risk Score</p>
                  <p
                    className="text-2xl font-semibold"
                    style={{ color: getRiskColor(selectedUser?.riskScore) }}
                  >
                    {selectedUser?.riskScore}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Events</p>
                  <p className="text-2xl font-semibold text-foreground">{selectedUser?.eventCount}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Recent Activity</h4>
              <div className="space-y-2">
                {selectedUser?.events?.slice(0, 5)?.map((event, index) => (
                  <div key={index} className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-foreground">
                        {event?.event_type?.replace(/_/g, ' ')?.toUpperCase()}
                      </span>
                      <span
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: `${getRiskColor(event?.severity === 'critical' ? 80 : 40)}15`,
                          color: getRiskColor(event?.severity === 'critical' ? 80 : 40)
                        }}
                      >
                        {event?.severity}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(event?.created_at)?.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-warning mb-1">Suspicious Activities</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedUser?.suspiciousActivities} suspicious activities detected
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="User" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Select a user to view details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserBehaviorAnalysis;